import Cancel from "@mui/icons-material/Cancel";
import CheckCircle from "@mui/icons-material/CheckCircle";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { WasteManifestSource } from "generated-graphql/graphql";

import { SOURCE_LABEL, SOURCE_LABEL_SHORT, SOURCE_ORDER } from "../manifestSourceDisplay";
import { getSourceAccent, SourceLetterBox } from "../SourceLetterBox";
import { SectionHeader } from "./SectionHeader";

interface ManifestDataSourcesProps {
  /** Sources currently providing data for this manifest. */
  availableSources: readonly WasteManifestSource[];
  /**
   * A manifest document pillar exists but its extracted data hasn't been
   * human-reviewed (flagged for review, never reviewed, or extraction failed)
   * — so the Document source is treated as unavailable. Renders a "pending
   * review" marker beside the Document chip. Surfaced to all users: the
   * document itself stays viewable (the drawer's pending-review banner carries
   * the explainer and its actions); only its data is gated until the document
   * is reviewed.
   */
  documentPendingReview?: boolean;
  /**
   * When provided, the pending-review marker beside the Document chip becomes a
   * button that opens the manifest-document review screen, where the extracted
   * data is reviewed in place. Omitted for callers without review access, where
   * the marker stays a static, tooltip-only indicator — and only there does its
   * tooltip name Encamp as the reviewer, because only there is that true.
   */
  onReviewDocument?: () => void;
}

/**
 * Pending-review marker beside the Document chip: a document glyph flagging that
 * the manifest's document data hasn't been reviewed yet. Clickable (opens the
 * review screen) when `onReview` is provided, otherwise a static tooltip-only
 * indicator.
 */
function PendingReviewMarker({ onReview }: { onReview?: () => void }) {
  if (!onReview) {
    return (
      <Tooltip title="Manifest document data is pending review by Encamp staff" placement="right">
        <DescriptionOutlined
          sx={{ fontSize: "1.125rem", color: "warning.main" }}
          aria-label="Manifest document data is pending review by Encamp staff"
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Review manifest document data" placement="right">
      <IconButton
        size="small"
        onClick={onReview}
        aria-label="Review manifest document data"
        sx={{ p: 0 }}
      >
        <DescriptionOutlined sx={{ fontSize: "1.125rem", color: "warning.main" }} />
      </IconButton>
    </Tooltip>
  );
}

/** Leading icon inherits the chip's text color (check when present, X when missing). */
const ICON_INHERIT_SX = { "& .MuiChip-icon": { color: "inherit" } } as const;

/** Hierarchy + priority explainer behind the header's info icon. */
function DataSourcesExplainer() {
  const theme = useTheme();
  return (
    <Stack spacing={1} sx={{ p: 0.5, maxWidth: 340 }}>
      <Typography variant="caption">
        We collect manifest data from multiple sources. Each data field below shows the value from
        its highest-priority available source, in this order of precedence:
      </Typography>
      <Stack spacing={0.25} alignItems="flex-start">
        {SOURCE_ORDER.map((source, index) => (
          <Stack key={source} spacing={0.25} alignItems="flex-start">
            {index > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                ↓
              </Typography>
            )}
            <Chip
              label={SOURCE_LABEL[source]}
              size="small"
              sx={{
                color: getSourceAccent(theme, source),
                bgcolor: alpha(getSourceAccent(theme, source), 0.12),
              }}
            />
          </Stack>
        ))}
      </Stack>
      {/* pt (not mt): Stack spacing overrides child margins, padding survives. */}
      <Typography variant="caption" sx={{ pt: 1 }}>
        Data fields marked with{" "}
        <Box component="span" sx={{ display: "inline-flex", gap: 0.5, mx: 0.25 }}>
          {SOURCE_ORDER.map((source) => (
            <SourceLetterBox key={source} source={source} size={14} tooltip={null} />
          ))}
        </Box>{" "}
        are only present on that data source. Differences are called out with an{" "}
        <ErrorOutline
          sx={{ fontSize: "0.875rem", color: "warning.main", verticalAlign: "text-bottom" }}
        />{" "}
        icon.
      </Typography>
      <Typography variant="caption">
        Manifest document data is not available until an attached manifest document (PDF) has been
        processed and reviewed.
      </Typography>
    </Stack>
  );
}

/**
 * Compact "Data Sources" panel inside the Summary box: a soft chip per data
 * source (Document / e-Manifest / Vendor, in priority order) with a check
 * icon, greyed with an X when that source is missing. A document pending
 * review counts as missing — all users see a "pending review" icon beside the
 * Document chip; the drawer's pending-review banner carries the explainer and a
 * "View document" action, making clear the document is viewable even though its
 * data isn't yet a manifest record. The header's info icon explains the
 * hierarchy and priority order.
 */
export function ManifestDataSources({
  availableSources,
  documentPendingReview,
  onReviewDocument,
}: ManifestDataSourcesProps) {
  const theme = useTheme();

  return (
    <Box>
      <SectionHeader title="Data Sources">
        <InfoIconTooltip
          tooltip={<DataSourcesExplainer />}
          fontSize="1rem"
          iconButtonSx={{ p: 0 }}
        />
      </SectionHeader>
      <Stack spacing={0.75} alignItems="flex-start">
        {SOURCE_ORDER.map((source) => {
          const pendingReviewIcon =
            source === WasteManifestSource.ManifestDocument && documentPendingReview ? (
              <PendingReviewMarker onReview={onReviewDocument} />
            ) : null;

          if (!availableSources.includes(source)) {
            return (
              <Stack key={source} direction="row" spacing={0.75} alignItems="center">
                <Chip
                  label={SOURCE_LABEL_SHORT[source]}
                  size="small"
                  disabled
                  icon={<Cancel />}
                  sx={ICON_INHERIT_SX}
                />
                {pendingReviewIcon}
              </Stack>
            );
          }
          const color = getSourceAccent(theme, source);
          return (
            <Stack key={source} direction="row" spacing={0.75} alignItems="center">
              <Chip
                label={SOURCE_LABEL_SHORT[source]}
                size="small"
                icon={<CheckCircle />}
                sx={{ color, bgcolor: alpha(color, 0.08), ...ICON_INHERIT_SX }}
              />
              {pendingReviewIcon}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
